import React, { useState, useRef, useEffect } from 'react';
import { getSystemTooltip } from '../../data/systemTooltips';

/**
 * TooltipBadge — small "(?)" icon that shows a system explanation on
 * hover (desktop) or tap (touch). Reads its content from the central
 * systemTooltips catalog by `id`.
 *
 * Props:
 *   id      — key into systemTooltips.js (e.g. 'market.commission')
 *   inline  — bool, defaults true. If false, renders as a block w/ margin.
 */
function TooltipBadge({ id, inline = true, size = 14 }) {
  const tip = getSystemTooltip(id);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  if (!tip) return null;

  const containerStyle = {
    position: 'relative',
    display: inline ? 'inline-flex' : 'flex',
    alignItems: 'center',
    verticalAlign: 'middle',
    marginLeft: 4,
    cursor: 'help',
  };

  const iconStyle = {
    width: size,
    height: size,
    lineHeight: `${size}px`,
    borderRadius: '50%',
    background: 'rgba(120, 170, 220, 0.18)',
    border: '1px solid rgba(120, 170, 220, 0.45)',
    color: '#cfe6ff',
    fontSize: Math.max(10, size - 4),
    fontWeight: 600,
    textAlign: 'center',
    userSelect: 'none',
    fontFamily: 'serif',
  };

  const popoverStyle = {
    position: 'absolute',
    bottom: `calc(100% + 6px)`,
    left: '50%',
    transform: 'translateX(-50%)',
    minWidth: 220,
    maxWidth: 300,
    padding: '8px 10px',
    background: 'rgba(18, 24, 36, 0.97)',
    border: '1px solid rgba(120, 170, 220, 0.45)',
    borderRadius: 6,
    color: '#dde8f5',
    fontSize: 12,
    lineHeight: 1.4,
    zIndex: 10000,
    boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
    pointerEvents: 'none',
    whiteSpace: 'pre-line',
    textAlign: 'left',
  };

  return (
    <span
      ref={wrapRef}
      style={containerStyle}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
      aria-label={tip.title}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
    >
      <span style={iconStyle}>?</span>
      {open && (
        <span style={popoverStyle}>
          <strong style={{ color: '#9fdcf7', display: 'block', marginBottom: 3 }}>
            {tip.title}
          </strong>
          {tip.body}
        </span>
      )}
    </span>
  );
}

export default TooltipBadge;
